import React, { useState, useEffect } from 'react';
import { knowledgeBaseApi } from '../../services/knowledgeBaseApi';
import AIExplainButton from './AIExplainButton';
import ConfigDiff from './ConfigDiff';
import VersionHistory from './VersionHistory';
import './AIConfigEditor.css';

interface AIConfigEditorProps {
  config: any;
  configType: string;
  configName: string;
  onConfigUpdated: (updatedConfig: any) => void;
  onCancel?: () => void;
}

const AIConfigEditor: React.FC<AIConfigEditorProps> = ({
  config,
  configType,
  configName,
  onConfigUpdated,
  onCancel
}) => {
  const [editMode, setEditMode] = useState(false);
  const [editRequest, setEditRequest] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewConfig, setPreviewConfig] = useState<any>(null);
  const [changeSummary, setChangeSummary] = useState<string>('');
  const [showDiff, setShowDiff] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [titleValue, setTitleValue] = useState(config?.name || configName);
  const [viewMode, setViewMode] = useState<'json' | 'natural'>('natural');
  const [showVersionHistory, setShowVersionHistory] = useState(false);

  // Helper function to get all keys that should be expanded
  const getAllSectionKeys = (obj: any, parentKey: string = '', depth: number = 0): string[] => {
    const keys: string[] = [];
    
    if (typeof obj === 'object' && obj !== null && !Array.isArray(obj)) {
      Object.keys(obj).forEach(key => {
        const sectionKey = `${key}-${depth + 1}`;
        keys.push(sectionKey);
        // Recursively get nested keys
        const nestedKeys = getAllSectionKeys(obj[key], key, depth + 1);
        keys.push(...nestedKeys);
      });
    }
    
    return keys;
  };

  // Initialize with all sections expanded
  useEffect(() => {
    const currentConfig = previewConfig || config;
    const allKeys = new Set<string>();
    
    // Add the root key that matches what renderJsonValue uses
    allKeys.add(`${configName}-0`);
    
    // Add all nested keys
    const nestedKeys = getAllSectionKeys(currentConfig, configName, 0);
    nestedKeys.forEach(key => allKeys.add(key));
    
    setExpandedSections(allKeys);
  }, [config, previewConfig, configName]);

  // Update title value when config changes
  useEffect(() => {
    setTitleValue(config?.name || configName);
  }, [config, configName]);

  const handleEditSubmit = async () => {
    if (!editRequest.trim()) {
      setError('Please describe the changes you want to make');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await knowledgeBaseApi.previewConfigEditWithAI(
        configType,
        configName,
        editRequest
      );

      if (result.success) {
        setPreviewConfig(result.preview_config);
        setChangeSummary(result.change_summary);
        setEditMode(false);
        setShowDiff(true);
      } else {
        setError('Failed to generate preview');
      }
    } catch (err: any) {
      setError(err.message || 'Error generating preview');
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptChanges = async () => {
    if (!previewConfig) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const result = await knowledgeBaseApi.acceptConfigEdit(
        configType,
        configName,
        previewConfig,
        editRequest,
        changeSummary
      );
      
      if (result.success) {
        onConfigUpdated(result.config);
        setPreviewConfig(null);
        setEditRequest('');
        setChangeSummary('');
        setShowDiff(false);
      } else {
        setError('Failed to save changes');
      }
    } catch (err: any) {
      setError(err.message || 'Error saving changes');
    } finally {
      setLoading(false);
    }
  };

  const handleRejectChanges = () => {
    setPreviewConfig(null);
    setEditRequest('');
    setChangeSummary('');
    setShowDiff(false);
  };

  const handleTitleSave = async () => {
    if (!titleValue.trim() || titleValue === configName) {
      setIsEditingTitle(false);
      setTitleValue(configName);
      return;
    }

    try {
      // Update the config with the new name
      const updatedConfig = { ...config, name: titleValue };
      onConfigUpdated(updatedConfig);
      setIsEditingTitle(false);
    } catch (err) {
      console.error('Error updating title:', err);
      setTitleValue(configName);
      setIsEditingTitle(false);
    }
  };

  const handleTitleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleTitleSave();
    } else if (e.key === 'Escape') {
      setTitleValue(configName);
      setIsEditingTitle(false);
    }
  };

  const toggleSection = (section: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(section)) {
      newExpanded.delete(section);
    } else {
      newExpanded.add(section);
    }
    setExpandedSections(newExpanded);
  };

  const expandAll = () => {
    const currentConfig = previewConfig || config;
    const allKeys = new Set<string>();
    
    // Get all possible section keys for the current view
    const collectAllKeys = (obj: any, parentKey: string = '', depth: number = 0) => {
      if (typeof obj === 'object' && obj !== null && !Array.isArray(obj)) {
        Object.keys(obj).forEach(key => {
          // Add keys in the format used by both views
          allKeys.add(`${key}-${depth}`);
          allKeys.add(`${parentKey}-${key}-${depth}`);
          // Recursively collect nested keys
          collectAllKeys(obj[key], `${parentKey}-${key}`, depth + 1);
        });
      }
    };
    
    // Add root key
    allKeys.add(`${configName}-0`);
    collectAllKeys(currentConfig, configName, 0);
    
    setExpandedSections(allKeys);
  };

  const collapseAll = () => {
    setExpandedSections(new Set());
  };

  const renderJsonValue = (value: any, key: string, depth: number = 0): JSX.Element => {
    // For primitive values
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      return (
        <span className="json-value-primitive">
          {typeof value === 'string' ? `"${value}"` : String(value)}
        </span>
      );
    }

    // For arrays
    if (Array.isArray(value)) {
      if (value.length === 0) {
        return <span className="json-value-array">[]</span>;
      }

      // For arrays of primitives, show inline
      if (value.every(item => typeof item === 'string' || typeof item === 'number' || typeof item === 'boolean')) {
        return (
          <div className="json-value-array">
            [
            {value.map((item, index) => (
              <div key={index} className="json-array-item" style={{ marginLeft: '20px' }}>
                {typeof item === 'string' ? `"${item}"` : String(item)}
                {index < value.length - 1 && ','}
              </div>
            ))}
            ]
          </div>
        );
      }

      // For arrays of objects
      return (
        <div className="json-value-array">
          [
          {value.map((item, index) => (
            <div key={index} style={{ marginLeft: '20px' }}>
              {renderJsonValue(item, `${key}[${index}]`, depth + 1)}
              {index < value.length - 1 && ','}
            </div>
          ))}
          ]
        </div>
      );
    }

    // For objects
    if (typeof value === 'object' && value !== null) {
      const entries = Object.entries(value);
      if (entries.length === 0) {
        return <span className="json-value-object">{'{}'}</span>;
      }

      const sectionKey = `${key}-${depth}`;
      const isExpanded = expandedSections.has(sectionKey);

      return (
        <div className="json-value-object">
          <span 
            className="json-section-toggle"
            onClick={() => toggleSection(sectionKey)}
          >
            {isExpanded ? '▼' : '▶'} {'{'}
          </span>
          {isExpanded && (
            <div style={{ marginLeft: '20px' }}>
              {entries.map(([k, v], index) => (
                <div key={k} className="json-property">
                  <span className="json-key">"{k}"</span>
                  {/* Add explain button for major sections at depth 1 */}
                  {depth === 0 && typeof v === 'object' && v !== null && !Array.isArray(v) && (
                    <AIExplainButton
                      configSection={v}
                      sectionName={k}
                      buttonText="?"
                      className="section-explain-inline"
                    />
                  )}
                  : {renderJsonValue(v, k, depth + 1)}
                  {index < entries.length - 1 && ','}
                </div>
              ))}
            </div>
          )}
          {isExpanded && '}'}
        </div>
      );
    }

    return <span>null</span>;
  };

  const renderNaturalValue = (value: any, key: string, depth: number = 0): JSX.Element => {
    // For primitive values
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      return (
        <span className="natural-value-primitive">
          {String(value)}
        </span>
      );
    }

    // For arrays
    if (Array.isArray(value)) {
      if (value.length === 0) {
        return <span className="natural-value-empty">None</span>;
      }

      // For arrays of primitives, show as simple list
      if (value.every(item => typeof item === 'string' || typeof item === 'number' || typeof item === 'boolean')) {
        return (
          <div className="natural-value-array">
            {value.map((item, index) => (
              <div key={index} className="natural-array-item" style={{ marginLeft: '20px' }}>
                {String(item)}
              </div>
            ))}
          </div>
        );
      }

      // For arrays of objects
      return (
        <div className="natural-value-array">
          {value.map((item, index) => (
            <div key={index} style={{ marginLeft: '20px', marginTop: index > 0 ? '12px' : '0' }}>
              {renderNaturalValue(item, `${key}[${index}]`, depth + 1)}
            </div>
          ))}
        </div>
      );
    }

    // For objects
    if (typeof value === 'object' && value !== null) {
      const entries = Object.entries(value);
      if (entries.length === 0) {
        return <span className="natural-value-empty">None</span>;
      }

      // Check if this object has nested objects/arrays that should be collapsible
      const hasNestedObjects = entries.some(([_, v]) => 
        typeof v === 'object' && v !== null && (Array.isArray(v) ? v.length > 0 : Object.keys(v).length > 0)
      );

      return (
        <div className="natural-value-object">
          {entries.map(([k, v], index) => {
            const sectionKey = `${key}-${k}-${depth}`;
            const isExpanded = expandedSections.has(sectionKey);
            const isObjectOrArray = typeof v === 'object' && v !== null;
            const hasContent = isObjectOrArray && (Array.isArray(v) ? v.length > 0 : Object.keys(v).length > 0);
            
            return (
              <div key={k} className="natural-property" style={{ 
                marginTop: index > 0 ? (depth === 0 ? '16px' : '12px') : '0',
                marginLeft: depth > 0 ? '20px' : '0'
              }}>
                {isObjectOrArray && hasContent ? (
                  <>
                    <div 
                      className={depth === 0 ? "natural-key" : "natural-key-nested"}
                      style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }} 
                      onClick={() => toggleSection(sectionKey)}
                    >
                      <span style={{ fontSize: '12px' }}>{isExpanded ? '▼' : '▶'}</span>
                      <span>{k.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}{depth > 0 ? ':' : ''}</span>
                      {depth === 0 && !Array.isArray(v) && (
                        <AIExplainButton
                          configSection={v}
                          sectionName={k}
                          buttonText="?"
                          className="section-explain-inline"
                        />
                      )}
                    </div>
                    {isExpanded && (
                      <div className="natural-value">
                        {renderNaturalValue(v, `${key}-${k}`, depth + 1)}
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div className={depth === 0 ? "natural-key" : "natural-key-inline"}>
                      {k.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}{depth > 0 ? ':' : ''}
                    </div>
                    <div className="natural-value" style={{ marginLeft: depth === 0 ? '20px' : '0' }}>
                      {renderNaturalValue(v, `${key}-${k}`, depth + 1)}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      );
    }

    return <span>None</span>;
  };

  // Just show the config as-is, no filtering needed
  const currentConfigToShow = previewConfig || config;

  return (
    <div className="ai-config-editor">
      <div className="editor-header">
        <div className="title-section">
          {isEditingTitle ? (
            <input
              type="text"
              value={titleValue}
              onChange={(e) => setTitleValue(e.target.value)}
              onBlur={handleTitleSave}
              onKeyDown={handleTitleKeyPress}
              className="title-edit-input"
              autoFocus
            />
          ) : (
            <h3>
              {config?.is_system && (
                <span className="system-badge" title="System framework - cannot be renamed">🔒</span>
              )}
              {config?.name || configName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
              {!config?.is_system && (
                <button
                  className="btn-edit-title"
                  onClick={() => setIsEditingTitle(true)}
                  title="Edit title"
                >
                  ✏️
                </button>
              )}
            </h3>
          )}
        </div>
        <div className="editor-actions">
          <div className="view-controls">
            <button
              className="btn-expand-collapse"
              onClick={expandAll}
              title="Expand all sections"
            >
              ⊞
            </button>
            <button
              className="btn-expand-collapse"
              onClick={collapseAll}
              title="Collapse all sections"
            >
              ⊟
            </button>
            <button
              className="view-toggle"
              onClick={() => setViewMode(viewMode === 'json' ? 'natural' : 'json')}
              title={`Switch to ${viewMode === 'json' ? 'Natural' : 'JSON'} view`}
            >
              {viewMode === 'json' ? '📝' : '{ }'} {viewMode === 'json' ? 'Natural' : 'JSON'}
            </button>
            <button
              className="btn-version-history"
              onClick={() => setShowVersionHistory(!showVersionHistory)}
              title="View version history"
            >
              📚 History
            </button>
          </div>
          {!editMode && !previewConfig && (
            <button
              className="btn btn-ai-edit"
              onClick={() => setEditMode(true)}
            >
              ✨ AI Edit
            </button>
          )}
          {previewConfig && (
            <>
              <button
                className="btn btn-toggle-view"
                onClick={() => setShowDiff(!showDiff)}
              >
                {showDiff ? '📄 View Config' : '🔍 View Diff'}
              </button>
              <button
                className="btn btn-accept"
                onClick={handleAcceptChanges}
                disabled={loading}
              >
                {loading ? 'Saving...' : '✓ Accept'}
              </button>
              <button
                className="btn btn-reject"
                onClick={handleRejectChanges}
                disabled={loading}
              >
                ✗ Reject
              </button>
            </>
          )}
        </div>
      </div>

      {editMode && (
        <div className="ai-edit-panel">
          <h4>Enhance or Update {configType === 'domain' ? 'Domain Rules' : configType === 'task' ? 'Task Framework' : 'Safety Checks'}</h4>
          <div className="edit-guidance">
            <div className="guidance-icon">ℹ️</div>
            <div className="guidance-text">
              <strong>AI will make ONLY the specific changes you request</strong> - no creative additions or restructuring.
              {configType === 'domain' && ' Perfect for adding new rules, updating thresholds, or enhancing existing policies.'}
              {' '}Be explicit about what to add, remove, or modify.
            </div>
          </div>
          <textarea
            value={editRequest}
            onChange={(e) => setEditRequest(e.target.value)}
            placeholder={
              configType === 'domain' 
                ? "Examples:\n• Add invoice approval matrix with thresholds\n• Update payment terms to Net 45\n• Include new compliance requirements for SOX\n• Enhance segregation of duties controls"
                : "Describe what you want to add, change, or remove..."
            }
            rows={5}
            disabled={loading}
          />
          {error && <div className="error-message">{error}</div>}
          <div className="edit-actions">
            <button
              onClick={handleEditSubmit}
              disabled={loading || !editRequest.trim()}
              className="btn btn-primary"
            >
              {loading ? 'Applying...' : 'Apply Changes'}
            </button>
            <button
              onClick={() => {
                setEditMode(false);
                setEditRequest('');
                setError(null);
              }}
              disabled={loading}
              className="btn btn-secondary"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {previewConfig && showDiff && (
        <>
          <div className="preview-notice">
            <strong>Preview Mode:</strong> Review the changes below. Accept or reject to continue.
          </div>
          <ConfigDiff
            original={config}
            modified={previewConfig}
            changeRequest={editRequest}
            changeSummary={changeSummary}
          />
        </>
      )}

      {(!showDiff || !previewConfig) && (
        <div className={`config-${viewMode}-view`}>
          <div className={`${viewMode}-content`}>
            {viewMode === 'json' 
              ? renderJsonValue(currentConfigToShow, configName)
              : renderNaturalValue(currentConfigToShow, configName)
            }
          </div>
        </div>
      )}

      {showVersionHistory && (
        <VersionHistory
          configType={configType}
          configName={configName}
          currentVersion={config?.version}
          onVersionRestore={() => {
            // Reload the config after restore
            window.location.reload();
          }}
        />
      )}
    </div>
  );
};

export default AIConfigEditor;